package fr.frux.core.designsystem.components

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import coil3.compose.AsyncImage
import coil3.compose.AsyncImagePainter
import coil3.compose.LocalPlatformContext
import coil3.request.ImageRequest
import fr.frux.core.common.logging.Logger
import coil3.size.Size as CoilSize

private const val TAG = "FruxRemoteImage"

/**
 * Image distante avec repli, seul point d'entrée Coil du repo (même confinement que MapLibre dans
 * `core-maps`) : aucun type Coil ne doit fuiter hors de ce module.
 *
 * [fallback] reste visible tant que le chargement n'a pas atteint [AsyncImagePainter.State.Success]
 * (loading, erreur, ou `url == null`) — jamais de `SubcomposeAsyncImage`, qui jank en LazyColumn.
 * Le crossfade se règle sur l'`ImageLoader` singleton (une seule fois), pas requête par requête.
 *
 * [successOverlay] est rendu au-dessus de l'image une fois seulement l'état `Success` atteint
 * (#389) : un appelant qui doit annoncer quelque chose *sur* la photo (mention, crédit) sans jamais
 * l'écrire au-dessus d'un pictogramme de repli. Coil reste confiné à ce fichier, l'appelant ne voit
 * jamais [AsyncImagePainter.State].
 *
 * [onIntrinsicSize] livre la taille propre de l'image chargée (zoom de la visionneuse, #686), sans
 * exposer le painter Coil. [decodeAtOriginalSize] désactive le décodage à la taille du cadre, que
 * Coil fait par défaut : une image zoomée doit garder ses pixels, sinon le zoom agrandit une
 * vignette floue.
 *
 * [contentDescription] n'est jamais posé sur l'`AsyncImage` elle-même : tant que le chargement n'a
 * pas atteint `Success`, TalkBack ne doit jamais annoncer une photo qui n'est pas réellement là
 * (repli visuel = icône + texte, déjà sémantique par eux-mêmes). Une fois `Success` atteint, la
 * description voyage sur une sémantique dédiée superposée à l'image (#389).
 */
@Composable
fun FruxRemoteImage(
    url: String?,
    contentDescription: String?,
    modifier: Modifier = Modifier,
    contentScale: ContentScale = ContentScale.Crop,
    fallback: @Composable () -> Unit = {},
    successOverlay: @Composable BoxScope.() -> Unit = {},
    onIntrinsicSize: (Size) -> Unit = {},
    decodeAtOriginalSize: Boolean = false,
) {
    if (url == null) {
        fallback()
        return
    }

    Box(modifier = modifier) {
        var isSuccess by remember(url) { mutableStateOf(false) }
        if (!isSuccess) {
            fallback()
        }
        val platformContext = LocalPlatformContext.current
        val model =
            remember(url, decodeAtOriginalSize) {
                if (decodeAtOriginalSize) {
                    ImageRequest
                        .Builder(platformContext)
                        .data(url)
                        .size(CoilSize.ORIGINAL)
                        .build()
                } else {
                    url
                }
            }
        AsyncImage(
            model = model,
            contentDescription = null,
            contentScale = contentScale,
            onState = { state ->
                isSuccess = state is AsyncImagePainter.State.Success
                if (state is AsyncImagePainter.State.Success) onIntrinsicSize(state.painter.intrinsicSize)
                // Ce silence a masqué une session entière de debug (client Ktor partagé avec
                // Coil, ConnectTimeoutException jamais tracée) — cf. commentaire App.kt.
                if (state is AsyncImagePainter.State.Error) {
                    Logger.e(TAG, "Échec de chargement de l'image : $url", state.result.throwable)
                }
            },
            modifier = Modifier.matchParentSize(),
        )
        if (isSuccess) {
            if (contentDescription != null) {
                Box(
                    modifier =
                        Modifier.matchParentSize().semantics {
                            this.contentDescription = contentDescription
                        },
                )
            }
            successOverlay()
        }
    }
}
